// Upgrade.tsx
import React, { useEffect, useRef } from 'react';
import { FaArrowRight, FaCheckCircle } from 'react-icons/fa';
import { Link } from 'react-router-dom';

const Upgrade: React.FC = () => {

    const upgradeDesktopDivRef = useRef<HTMLElement>(null);

    useEffect(() => {
        const hash = window.location.hash;
        if (hash === '#desktop' && upgradeDesktopDivRef.current) {
            const headerOffset = 90; // Height of your sticky header in pixels
            const elementPosition = upgradeDesktopDivRef.current.getBoundingClientRect().top + window.pageYOffset;
            const offsetPosition = elementPosition - headerOffset;

            // Timeout to ensure the element is rendered before scrolling
            setTimeout(() => {
                window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
            }, 100); // Increased timeout slightly for potentially complex layouts
        }
    }, []);

    return (
        <div className="flex flex-col items-center justify-center setup-guide bg-dark-blue text-gray-300 p-4 h-full w-[100em] pb-10">
            {/* Page Title */}
            <h1 className="text-4xl text-center text-white mb-4 mt-4">Upgrade to Pro</h1>
            <p className="text-xl text-center text-gray-400 mb-6">
                Explore all the available features in Deck Hopper.
            </p>
            <hr className="border-t border-gray-600 w-2/3 mb-8" />



            {/* Why Upgrade Section */}
            <section className="w-full max-w-[70em] bg-bgBorderBlue bg-opacity-30 rounded-lg shadow-lg p-8 mb-8">
                <h3 className="text-3xl mb-6 text-white text-center">Why Upgrade to Pro?</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {/* Feature Item */}
                    <div className="flex items-start">
                        <FaCheckCircle className="text-green-500 text-2xl mr-4 mt-1" />
                        <div>
                            <h3 className="text-xl mb-2">Remove All Ads</h3>
                            <p className="text-gray-400">Enjoy an uninterrupted experience without any ads.</p>
                        </div>
                    </div>
                    {/* Feature Item */}
                    <div className="flex items-start">
                        <FaCheckCircle className="text-green-500 text-2xl mr-4 mt-1" />
                        <div>
                            <h3 className="text-xl mb-2">Access to Pro Features</h3>
                            <p className="text-gray-400">Unlock all advanced functionalities exclusive to Pro users.</p>
                        </div>
                    </div>
                    {/* Feature Item */}
                    <div className="flex items-start">
                        <FaCheckCircle className="text-green-500 text-2xl mr-4 mt-1" />
                        <div>
                            <h3 className="text-xl  mb-2">Support the Developer</h3>
                            <p className="text-gray-400">Help fund the development of Deck Hopper and future updates.</p>
                        </div>
                    </div>
                </div>
                <div className="flex justify-center mt-8">
                    <Link to="/features">
                        <button className="inline-flex items-center px-6 py-3 bg-transparent border border-green-500 text-green-500  rounded-md shadow-lg hover:bg-green-500 hover:text-white transition duration-300">
                            See All Features <FaArrowRight className="ml-2" />
                        </button>
                    </Link>
                </div>
            </section>

            {/* Upgrade to Pro on Mobile */}

            <section className="w-full max-w-[70em] bg-bgBorderBlue bg-opacity-30 rounded-lg shadow-lg p-8 mb-8" id="upgradenow" ref={upgradeDesktopDivRef}>
                <div className="flex flex-col lg:flex-row items-start gap-8">
                    <div className="flex-1">
                        <h3 className="text-2xl  mb-4 text-white">Upgrade on Android</h3>
                        <ol className="list-decimal list-inside space-y-4 text-gray-400">
                            <li>
                                <b>Download Deck Hopper</b> from the{' '}
                                <a
                                    href="https://play.google.com/store/apps/details?id=ca.christinak.DeckHopper"
                                    className="text-amber-500 hover:text-amber-400 underline"
                                >
                                    Google Play Store
                                </a>
                                .
                            </li>
                            <li>
                                Open Deck Hopper, navigate to <b>Menu → Account</b>, click on <b>'Purchase Pro'</b>.
                            </li>
                            <li>To restore your purchase on a new device, click on <b>'Restore Purchase'</b> in the same menu.</li>
                        </ol>
                    </div>
                </div>
            </section>
            <section className="w-full max-w-[70em] bg-bgBorderBlue bg-opacity-30 rounded-lg shadow-lg p-8 mb-8" id="upgradenow">

                <div id="desktop" className="flex flex-col lg:flex-row gap-8">
                    <div>
                        <h3 className="text-2xl  mb-4 text-white">Upgrade on Desktop</h3>
                        <ol className="list-decimal list-inside space-y-4 text-gray-400">
                            <li>
                                <b>Purchase Deck Hopper Pro</b> from{' '}
                                <a
                                    href="https://christinak.itch.io/deck-hopper-pro"
                                    className="text-amber-500 hover:text-amber-400 underline"
                                >
                                    Itch.io page
                                </a>{' '}
                                by donating at least <s className="text-red-500 opacity-70">$7.99</s> $5.99.
                            </li>
                            <li>
                                <b>Claim your download key</b> from the Itch.io page after purchase.
                            </li>
                            <li>
                                Open Deck Hopper, navigate to <b>Menu → Account</b>, and click on <b>'Register Download Key'</b>.
                            </li>
                        </ol>
                    </div>
                    <div>
                        <iframe
                            title="Deck Hopper Pro"
                            src="https://itch.io/embed/3377571?border_width=0&amp;bg_color=122645&amp;fg_color=dddada&amp;link_color=2563eb&amp;border_color=122645"
                            className="w-full md:w-[28rem] h-40 border-none"
                        >
                            <a href="https://christinak.itch.io/deck-hopper-pro">Deck Hopper Pro</a>
                        </iframe>
                    </div>
                </div>
            </section>

            <section className="w-full max-w-[70em] bg-bgBorderBlue bg-opacity-30 rounded-lg shadow-lg p-8 mb-8">
                <h3 className="text-2xl mb-4 text-white">Upgrade on iOS</h3>
                <p className="text-gray-400">
                    On iOS, purchase immediately unlocks the Pro version with all features—no trial or further upgrade needed. iOS purchases are standalone and cannot be shared or restored on Android or Desktop.
                </p>
            </section>
            <section className="w-full max-w-[70em] bg-bgBorderBlue bg-opacity-30 rounded-lg shadow-lg p-8 mb-8">
                <div>

                    <h3 className="text-2xl mb-4 text-white">Access Pro on Other Devices</h3>
                    <p className="text-gray-400">
                        After upgrading on Android or Desktop, you can restore and access Pro on those platforms by logging in with the same account. (iOS purchases remain separate.)
                    </p>
                </div>

            </section>


        </div>
    );
}

export default Upgrade;
